import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';

export interface CreatableTentacle {
  id: string;
  name: string;
}

function arrayMove<T>(arr: Array<T>, fromIndex: number, toIndex: number): Array<T> {
  const newArray = [...arr];
  const [movedElement] = newArray.splice(fromIndex, 1);
  newArray.splice(toIndex, 0, movedElement);
  return newArray;
}

export interface EditableTentaclesTable {
  tentacles: Array<CreatableTentacle>;
  onAddTentacle: () => void;
  onRemoveTentacle: (id: string) => void;
  onUpdateTentacle: (id: string, update: Partial<CreatableTentacle>) => void;
  onUpdateTentacles: (tentacles: Array<CreatableTentacle>) => void;
}

export default function EditableTentaclesTable({
  tentacles,
  onAddTentacle,
  onRemoveTentacle,
  onUpdateTentacle,
  onUpdateTentacles,
}: EditableTentaclesTable): JSX.Element {
  const getSectionRows = (): Array<Row> =>
    tentacles.map((tentacle) => ({
      id: tentacle.id,
      cells: [
        {
          key: 'name',
          contents: (
            <TextField
              value={tentacle.name}
              onChange={(e) => {
                const name = e.target.value;
                onUpdateTentacle(tentacle.id, {name});
              }}
              onBlur={(e) => {
                const name = e.target.value.trim();
                onUpdateTentacle(tentacle.id, {name});
              }}
              labelStyle={LabelStyle.HIDDEN}
            />
          ),
          cellClassName: css(styles.nameCell),
        },
      ],
      actionGroup: tentacles.length > 0 && (
        <Button
          buttonStyle={ButtonStyle.CIRCLE_ICON}
          size={ButtonSize.SMALL}
          icon="delete"
          isDropdown={false}
          iconAltText="Remove tentacle"
          onClick={() => {
            onRemoveTentacle(tentacle.id);
          }}
        />
      ),
      rowClassName: css(styles.sectionsTableRow),
    }));

  return (
    <div>
      <FieldLabel label="Tentacles" isRequired={true} />
      <ReorderableSimpleTable
        isDraggingDisabled={tentacles.length <= 1}
        headerCells={[
          {
            key: 'name',
            contents: 'Name',
            cellClassName: css(styles.nameCell),
          },
        ]}
        noRowsDisplay="No tentacles."
        onReorderRow={(id: string, newIndex: number) => {
          onUpdateTentacles(
            arrayMove(
              tentacles,
              tentacles.findIndex((s) => s.id === id),
              newIndex
            )
          );
        }}
        rows={getSectionRows()}
        footerCells={[
          {
            key: 'add',
            contents: (
              <Button
                className={css(styles.addButton)}
                buttonStyle={ButtonStyle.TEXT}
                size={ButtonSize.SMALL}
                icon="compose"
                text="Add tentacle"
                onClick={onAddTentacle}
              />
            ),
            cellClassName: css(styles.addButtonCell),
          },
        ]}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  sectionsTableRow: {
    padding: '7px 14px',
  },
  addButtonCell: {
    margin: '-4px auto',
  },
  addButton: {
    marginLeft: '-10px',
  },
  nameCell: {
    flexGrow: 2,
  },
});
